import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  EffectRef,
  ElementRef,
  inject,
  model,
  OnDestroy,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormControlStatus,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  AlertComponent,
  ButtonComponent,
  CardComponent,
  FormGroupComponent,
  IconComponent,
  RowComponent,
  TextComponent,
  TextInputComponent,
  ValidationFeedbackComponent,
} from 'core-ui';
import { catchError, distinctUntilChanged, filter, map, mergeMap, Observable, of, tap } from 'rxjs';

import { PistonLoaderComponent } from '../../../components/piston-loader/piston-loader.component';
import { APPOINTMENT_ROUTES } from '../../../consts/appointment-routes.const';
import { NumericInputDirective } from '../../../directives/numeric-input.directive';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { BackToEmailService } from '../../../services/back-to-email.service';
import { formServerError } from '../../../services/form-server-error';
import { PlanAppointmentQueryParams } from '../plan-appointment/license-plate-selection/interfaces/plan-appointment-query-params.interface';
import { AuthenticationCodesResponse } from './authentication-codes-response.interface';
import { AuthService } from './services/auth.service';
import { SessionService } from './services/session.service';

const INPUT_FIELDS_NUMBER = 6;
const HAS_SEND_NEW_AUTHENTICATION_CODE_VISUAL_INDICATION = 4000;
const AUTHENTICATION_MAIL_PARAM = 'authenticationMail';
// Codes-API statussen waarbij er geen SMS-validatie mogelijk is: 404 (e-mail niet
// gematcht) en 503 (gematcht, maar geen telefoonnummer → SMS-verzending faalt). In beide
// gevallen valt de bezoeker terug op de gast-flow (Situatie 2).
const GUEST_REDIRECT_STATUSES: readonly number[] = [404, 503];

@Component({
  selector: 'app-home',
  imports: [
    CardComponent,
    TextComponent,
    ReactiveFormsModule,
    FormGroupComponent,
    ButtonComponent,
    IconComponent,
    TextInputComponent,
    PistonLoaderComponent,
    ValidationFeedbackComponent,
    RowComponent,
    AlertComponent,
    NumericInputDirective,
    TranslatePipe,
  ],
  templateUrl: './authenticate.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './authenticate.scss',
})
export class AuthenticatePage implements OnDestroy {
  protected authFields = viewChildren<'authFields', ElementRef>('authFields', { read: ElementRef });

  private readonly formBuilder: FormBuilder = new FormBuilder();
  private readonly router: Router = inject(Router);
  private readonly authService: AuthService = inject(AuthService);
  private readonly sessionService: SessionService = inject(SessionService);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly backToEmailService: BackToEmailService = inject(BackToEmailService);

  protected loading: boolean = false;
  protected hasSilentApiResponseError: boolean = false;
  protected hasResendCodeRequest: boolean = false;
  protected hasError: boolean = false;
  protected serverError: string | null = null;
  // The query param is the source of truth, so "stuur een nieuwe code" and the code submit
  // still know who they are for when the opening start2fa never succeeded (429, 500, …).
  protected email: string =
    (this.activatedRoute.snapshot.queryParams as PlanAppointmentQueryParams)[
      AUTHENTICATION_MAIL_PARAM
    ] ?? '';
  protected submitted: boolean = false;
  protected isSubmitting: boolean = false;
  private leavingToStart: boolean = false;

  private resendCodeTimeoutHandle: ReturnType<typeof setTimeout> | null = null;

  protected readonly authForm: FormGroup = this.formBuilder.group({
    validationCodes: this.formBuilder.array(
      Array.from(
        { length: INPUT_FIELDS_NUMBER },
        (): FormControl<string | null> => this.formBuilder.control(null, Validators.required),
      ),
    ),
  });

  protected readonly authStart = toSignal(
    this.activatedRoute.queryParams
      // Al ingelogd? Stop vóór start2fa: toSignal subscribet eager (field-init,
      // vóór de constructor-redirect) → anders alsnog een SMS bij terugkeer.
      .pipe(filter((): boolean => !this.sessionService.isAuthenticated()))
      .pipe(
        filter(
          (params: PlanAppointmentQueryParams): boolean => !!params[AUTHENTICATION_MAIL_PARAM],
        ),
      )
      .pipe(map((params: Params): string => params[AUTHENTICATION_MAIL_PARAM]))
      // De dealer-id safety-net (app.ts) hangt ná de eerste navigatie alsnog
      // ?dealerId= aan de URL → queryParams emit een tweede keer met hetzelfde
      // e-mailadres. Zonder distinctUntilChanged vuurt mergeMap dan een tweede
      // start2fa af → twee SMS-codes. Reageer dus alleen als de e-mail wijzigt.
      .pipe(distinctUntilChanged())
      .pipe(
        tap((email: string): void => {
          this.email = email;
          this.loading = true;
          this.serverError = null;
        }),
      )
      .pipe(
        mergeMap(
          (email: string): Observable<AuthenticationCodesResponse> =>
            this.authService.start2fa(email).pipe(
              tap((): void => {
                this.loading = false;
                this.changeDetectorRef.detectChanges();
              }),
              catchError(
                (error: unknown): Observable<AuthenticationCodesResponse> =>
                  this.handleStart2faError(email, error),
              ),
            ),
        ),
      ),
  );

  protected readonly lastTwoDigits = toSignal(
    toObservable(this.authStart)
      .pipe(
        filter(
          (
            res: AuthenticationCodesResponse | null | undefined,
          ): res is AuthenticationCodesResponse => !!res,
        ),
      )
      .pipe(map((res: AuthenticationCodesResponse): string => res.phoneNumberHint ?? '')),
  );

  public readonly value = model<string>('');

  private logEffect: EffectRef = effect((): void => {
    const inputFieldsArray = this.authFields();
    inputFieldsArray[0]?.nativeElement?.querySelector('input').focus();
  });
  public constructor() {
    if (this.sessionService.isAuthenticated()) {
      this.redirectToLicensePlateSelect();
      return;
    }

    for (let i: number = 0; i < INPUT_FIELDS_NUMBER - 1; i++) {
      this.validationCodes
        .get(`${i}`)
        ?.valueChanges.pipe(takeUntilDestroyed())
        .subscribe((value): void => {
          const inputFieldsArray = this.authFields();
          if (value !== '' && value !== null) {
            const nextInput = inputFieldsArray[i + 1];
            if (nextInput) {
              nextInput.nativeElement.querySelector('input').focus();
            }
          } else if (i === 0 && (value === '' || value === null)) {
            inputFieldsArray[0].nativeElement.querySelector('input').focus();
          }
        });
    }

    this.authForm.statusChanges
      .pipe(takeUntilDestroyed())
      .subscribe((status: FormControlStatus): void => {
        this.hasError = status === 'INVALID' && this.submitted;
      });

    if (
      !(this.activatedRoute.snapshot.queryParams as PlanAppointmentQueryParams)[
        'authenticationMail'
      ]
    ) {
      this.redirectToStart();
    }
  }

  public ngOnDestroy(): void {
    // logEffect wordt als field-initializer effect() automatisch door Angular opgeruimd.
    if (this.resendCodeTimeoutHandle !== null) {
      clearTimeout(this.resendCodeTimeoutHandle);
      this.resendCodeTimeoutHandle = null;
    }
  }
  protected get validationCodes(): FormArray {
    return this.authForm.get('validationCodes') as FormArray;
  }

  protected onKeyDown(event: KeyboardEvent, index: number): void {
    const inputFieldsArray = this.authFields();
    const currentInput = event.target as HTMLInputElement;

    const key: string = event.key.toLowerCase();

    // Enter wordt op form-niveau afgehandeld (keydown.enter → onEnterSubmit); hier niet
    // nóg eens, anders vuurt onSubmit dubbel → twee /sessions-requests.
    if (key === 'backspace' && !currentInput?.value && index > 0) {
      event.preventDefault();
      this.validationCodes.get(`${index}`)?.reset();
      const previousInput = inputFieldsArray[index - 1];

      if (previousInput) {
        previousInput.nativeElement.querySelector('input')?.focus();
        this.validationCodes.get(`${index}`)?.reset();
      }
    }
  }

  protected onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasteData = event.clipboardData?.getData('text') || '';

    pasteData.split('').forEach((char: string, i: number): void => {
      this.validationCodes.get(`${i}`)?.setValue(char, { emitEvent: true });
    });
  }

  protected reset(): void {
    const inputFieldsArray = this.authFields();
    inputFieldsArray.forEach((_, index: number): void => {
      this.validationCodes.get(`${index}`)?.reset();
    });
  }

  protected sendNewCode(mouseEvent: MouseEvent, email: string): void {
    mouseEvent.preventDefault();

    this.hasSilentApiResponseError = false;
    this.serverError = null;

    this.reset();
    this.authFields()[0]?.nativeElement?.querySelector('input').focus();

    this.hasResendCodeRequest = true;
    this.changeDetectorRef.detectChanges();
    this.authService
      .start2fa(email)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (): void => {
          if (this.resendCodeTimeoutHandle !== null) {
            clearTimeout(this.resendCodeTimeoutHandle);
          }
          this.resendCodeTimeoutHandle = setTimeout((): void => {
            this.resendCodeTimeoutHandle = null;
            this.hasResendCodeRequest = false;
            this.changeDetectorRef.detectChanges();
          }, HAS_SEND_NEW_AUTHENTICATION_CODE_VISUAL_INDICATION);
        },
        error: (error: unknown): void => {
          this.hasResendCodeRequest = false;
          this.serverError = formServerError(error);
          this.changeDetectorRef.detectChanges();
        },
      });
  }

  protected onEnterSubmit(): void {
    this.submitted = true;

    const hasFilledAllInputFields: boolean =
      Object.values(this.authForm.value.validationCodes).join('').length === INPUT_FIELDS_NUMBER;
    if (hasFilledAllInputFields) {
      this.onSubmit();
    }
  }

  protected onSubmit(): void {
    // Guard tegen een dubbele /sessions-request bij snel her-submitten (dubbele Enter
    // of dubbelklik) terwijl er nog een verzoek loopt.
    if (this.isSubmitting) {
      return;
    }
    this.submitted = true;

    this.isSubmitting = true;
    if (this.authForm.invalid) {
      this.hasError = true;
      this.isSubmitting = false;
      return;
    }

    this.serverError = null;
    this.hasSilentApiResponseError = false;

    const validationCode: string = Object.values(this.authForm.value.validationCodes).join('');
    this.authService
      .getAuthenticate(this.email, validationCode)
      .pipe(
        catchError((e): Observable<null> => {
          if (e.status === 401) {
            this.hasSilentApiResponseError = true;
          } else {
            this.serverError = formServerError(e);
          }
          this.isSubmitting = false;
          this.changeDetectorRef.detectChanges();
          return of(null);
        }),
      )
      .pipe(filter((res): res is AuthenticationCodesResponse => res !== null))
      .pipe(
        tap((res): void => {
          this.sessionService.setAuthenticated(res.email);
        }),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => {
        this.redirectToLicensePlateSelect().then((): void => {
          this.isSubmitting = false;
        });
      });
  }

  // Known customer skipping SMS: guest session, straight to the plate search (no vehicle list).
  protected continueWithoutLogin(): void {
    this.startGuestSession(this.email);
  }

  // Situatie 2: er kan geen SMS verstuurd worden (onbekende e-mail = 404, of geen
  // telefoonnummer = 503). Val terug op de gast-flow en stuur direct naar het
  // kentekeninvoerscherm. Overige fouten (bv. 429 of een serverfout) tonen we als melding
  // zodat de gebruiker opnieuw kan proberen.
  private handleStart2faError(
    email: string,
    error: unknown,
  ): Observable<AuthenticationCodesResponse> {
    if (this.cannotSendValidationSms(error)) {
      this.startGuestSession(email);
      return of({} as AuthenticationCodesResponse);
    }

    this.sessionService.logout();
    this.loading = false;
    this.serverError = formServerError(error);
    this.changeDetectorRef.detectChanges();
    return of({} as AuthenticationCodesResponse);
  }

  private cannotSendValidationSms(error: unknown): boolean {
    return error instanceof HttpErrorResponse && GUEST_REDIRECT_STATUSES.includes(error.status);
  }

  // Toon de loader tijdens de navigatie zodat noch het OTP-formulier noch een lege kaart
  // even flitst voordat we de bezoeker doorsturen.
  private startGuestSession(email: string): void {
    this.sessionService.setGuest(email);
    this.loading = true;
    this.changeDetectorRef.detectChanges();
    this.router.navigate([APPOINTMENT_ROUTES.LICENSE_PLATE_SEARCH], {
      queryParams: { redirect: 'REDIRECT_URL' },
      replaceUrl: true,
    });
  }

  protected redirectToStart(): void {
    this.router.navigate([APPOINTMENT_ROUTES.REDIRECT_URL]);
  }

  protected backToEmail(): void {
    if (this.leavingToStart) {
      return;
    }
    this.leavingToStart = true;
    this.backToEmailService.leave(this.email, 'authenticate');
  }

  protected redirectToLicensePlateSelect(): Promise<boolean> {
    return this.router.navigate([APPOINTMENT_ROUTES.LICENSE_PLATE_SELECTION], {
      replaceUrl: true,
    });
  }
}
